import { z } from "zod";
import type { EmailContent, SmsContent } from "./activityEvents";

export const CommsMessageSchema = z.object({
  id: z.string(),
  type: z.string(),
  from: z.string().nullable(),
  to: z.array(z.string()),
  cc: z.array(z.string()),
  bcc: z.array(z.string()),
  body: z.string().nullable(),
  subject: z.string().nullable(),
  direction: z.string(),
  provider: z.string(),
  externalId: z.string().nullable(),
  organizationId: z.string().nullable(),
  metadata: z.object({
    workflowRunId: z.string().optional(),
    agentSdkThreadId: z.string().optional(),
  }),
  idempotencyKey: z.string().nullable(),
  createdAt: z.coerce.date(),
  insertedAt: z.coerce.date(),
});

export type CommsMessage = z.infer<typeof CommsMessageSchema>;

export const GetMessageDataSchema = z.object({
  id: z.string(),
  type: z.string(),
  from: z.string().nullable(),
  to: z.union([z.string(), z.array(z.string())]),
  cc: z.array(z.string()),
  bcc: z.array(z.string()),
  body: z.string().nullable(),
  subject: z.string().nullable(),
  direction: z.string(),
  provider: z.string(),
  createdAt: z.coerce.date(),
});

export type MessageData = z.infer<typeof GetMessageDataSchema>;

/**
 * Extracts email content from a user message's metadata fields.
 * The fields store from, to, cc, bcc, subject as comma-separated strings.
 * @param fields - The messageMetadata.fields record from an SDK message
 * @param assetIds - Optional asset IDs associated with the message (e.g. inline images saved as attachments)
 * @returns Normalized EmailContent, or undefined if no email fields are present
 */
export const extractEmailContentFromMessageMetadata = (
  fields: Record<string, string> | undefined,
  assetIds?: string[],
): EmailContent | undefined => {
  if (!fields) {
    return undefined;
  }

  if (!fields.from && !fields.to) {
    return undefined;
  }

  return {
    from: fields.from || undefined,
    to: fields.to ? fields.to.split(",").filter(Boolean) : undefined,
    cc: fields.cc ? fields.cc.split(",").filter(Boolean) : undefined,
    bcc: fields.bcc ? fields.bcc.split(",").filter(Boolean) : undefined,
    subject: fields.subject || undefined,
    assetIds: assetIds && assetIds.length > 0 ? assetIds : undefined,
  };
};

const SEND_EMAIL_ACTION_IDS = new Set([
  "send_email_action",
  "email_send_action",
  "gmail_send_mail_forgepoint",
  "microsoft_outlook_send_mail_forgepoint",
  "smtp_send_email_forgepoint",
]);

/**
 * Checks whether an action is a send email action whose input
 * can be extracted into inline email content.
 * @param actionId - The action ID to check
 * @returns true if the action is a send email action
 */
export const isSendEmailAction = (actionId: string): boolean =>
  SEND_EMAIL_ACTION_IDS.has(actionId);

const RECEIVE_EMAIL_TRIGGER_IDS = new Set([
  "receive_email_trigger",
  "receive_email_trigger_v2",
  "imap-new-email",
  "gmail-new-email-received",
  "microsoft_outlook-new-email",
]);

/**
 * Checks whether a trigger/message type is a receive email trigger.
 * @param triggerId - The trigger ID or message type to check
 * @returns true if it is a receive email trigger
 */
export const isReceiveEmailTrigger = (triggerId: string): boolean =>
  RECEIVE_EMAIL_TRIGGER_IDS.has(triggerId);

/**
 * Checks whether an action is a send SMS action.
 * @param actionId - The action ID to check
 * @returns true if the action is a send SMS action
 */
export const isSendSmsAction = (actionId: string): boolean =>
  actionId === "send_sms_action";

/**
 * Checks whether a trigger/message type is a receive SMS trigger.
 * @param triggerId - The trigger ID or message type to check
 * @returns true if it is a receive SMS trigger
 */
export const isReceiveSmsTrigger = (triggerId: string): boolean =>
  triggerId === "receive_sms_trigger";

/**
 * Extracts SMS content from a send SMS action's input arguments.
 * @param actionId - The action ID
 * @param input - The action's input arguments (tool call arguments)
 * @returns Normalized SMS content, or undefined if not a send SMS action
 */
export const extractSmsContentFromActionInput = (
  actionId: string,
  input: unknown,
): SmsContent | undefined => {
  if (!isSendSmsAction(actionId) || !input || typeof input !== "object") {
    return undefined;
  }

  const args = input as Record<string, unknown>;

  return {
    to: asOptionalString(args.to),
    body: asOptionalString(args.body),
  };
};

/**
 * Extracts SMS content from a user message's metadata fields.
 * @param fields - The messageMetadata.fields record from an SDK message
 * @returns Normalized SmsContent, or undefined if no SMS fields are present
 */
export const extractSmsContentFromMessageMetadata = (
  fields: Record<string, string> | undefined,
): SmsContent | undefined => {
  if (!fields) {
    return undefined;
  }

  if (!fields.from && !fields.to) {
    return undefined;
  }

  return {
    from: fields.from || undefined,
    to: fields.to || undefined,
  };
};

/**
 * Extracts normalized email content from a send email action's input arguments.
 * Handles field name differences between action schemas:
 *  - send_email_action: to, subject, htmlBody/plainTextBody, cc, bcc
 *  - gmail_send_mail_forgepoint: recipients, subject, body, from, cc, bcc
 *  - email_send_action: recipients, subject, content, from, cc, bcc
 *  - microsoft_outlook_send_mail_forgepoint: recipients, subject, content, from, cc, bcc
 *  - smtp_send_email_forgepoint: recipients, subject, content, cc, bcc
 * @param actionId - The action ID
 * @param input - The action's input arguments (tool call arguments)
 * @returns Normalized email content, or undefined if not a send email action
 */
export const extractEmailContentFromActionInput = (
  actionId: string,
  input: unknown,
): EmailContent | undefined => {
  if (!isSendEmailAction(actionId) || !input || typeof input !== "object") {
    return undefined;
  }

  const args = input as Record<string, unknown>;
  const assetIds = toOptionalStringArray(args.assetIds);

  switch (actionId) {
    case "send_email_action":
      return {
        from: asOptionalString(args.forgepointMailAddress),
        to: toOptionalStringArray(args.to),
        cc: toOptionalStringArray(args.cc),
        bcc: toOptionalStringArray(args.bcc),
        subject: asOptionalString(args.subject),
        body:
          asOptionalString(args.plainTextBody) ??
          asOptionalString(args.htmlBody),
        assetIds,
      };

    case "gmail_send_mail_forgepoint":
      return {
        from: asOptionalString(args.from),
        to: toOptionalStringArray(args.recipients),
        cc: toOptionalStringArray(args.cc),
        bcc: toOptionalStringArray(args.bcc),
        subject: asOptionalString(args.subject),
        body: asOptionalString(args.body),
        assetIds,
      };

    case "email_send_action":
    case "microsoft_outlook_send_mail_forgepoint":
    case "smtp_send_email_forgepoint":
      return {
        from: asOptionalString(args.from),
        to: toOptionalStringArray(args.recipients),
        cc: toOptionalStringArray(args.cc),
        bcc: toOptionalStringArray(args.bcc),
        subject: asOptionalString(args.subject),
        body: asOptionalString(args.content),
        assetIds,
      };

    default:
      return undefined;
  }
};

/**
 * Extracts email content from a trigger output object (e.g. Gmail, Outlook, IMAP trigger outputs).
 * The trigger output contains email fields and an `assets` array with `{id, ...}` objects.
 * @param output - The trigger output as a JSON object
 * @returns Normalized EmailContent with assetIds, or undefined if not an email output
 */
export const extractEmailContentFromTriggerOutput = (
  output: Record<string, unknown>,
): EmailContent | undefined => {
  if (!output || typeof output !== "object") {
    return undefined;
  }

  const from = asOptionalString(output.from);
  const subject = asOptionalString(output.subject);
  const body = asOptionalString(output.body);

  if (!from && !body) {
    return undefined;
  }

  const to = toOptionalStringArray(output.to);
  const cc = toOptionalStringArray(output.cc);
  const bcc = toOptionalStringArray(output.bcc);
  const assetIds = extractAssetIdsFromTriggerOutput(output);

  return { from, to, cc, bcc, subject, body, assetIds };
};

/**
 * Extracts asset IDs from a trigger output's `assets` array.
 * @param output - The trigger output object
 * @returns Array of asset IDs, or undefined if none
 */
const extractAssetIdsFromTriggerOutput = (
  output: Record<string, unknown>,
): string[] | undefined => {
  if (!Array.isArray(output.assets)) {
    return undefined;
  }

  const ids = output.assets
    .filter(
      (asset): asset is { id: string } =>
        typeof asset === "object" &&
        asset !== null &&
        "id" in asset &&
        typeof asset.id === "string",
    )
    .map((asset) => asset.id);

  return ids.length > 0 ? ids : undefined;
};

const asOptionalString = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

/**
 * Normalizes an unknown value into a non-empty string array or undefined.
 * Handles single strings, string arrays, and filters non-string elements.
 * @param value - A string, string[], mixed array, or any other value
 * @returns A non-empty string array, or undefined
 */
const toOptionalStringArray = (value: unknown): string[] | undefined => {
  if (typeof value === "string") {
    return [value];
  }
  if (!Array.isArray(value)) {
    return undefined;
  }
  const arr = value.filter((v): v is string => typeof v === "string");
  return arr.length > 0 ? arr : undefined;
};

export const ItemCommsMessageSchema = z.object({
  commsMessageId: z.string(),
  referencedAt: z.coerce.date(),
  conversationId: z.string().nullable(),
  direction: z.string(),
  provider: z.string(),
  from: z.string().nullable(),
  senderLabel: z.string().nullable(),
  to: z.array(z.string()),
  subject: z.string().nullable(),
  body: z.string().nullable(),
  createdAt: z.coerce.date(),
});

export type ItemCommsMessage = z.infer<typeof ItemCommsMessageSchema>;

/** Response for GET /items/:itemId/comms-messages — chronological order. */
export const ItemCommsMessagesResponseSchema = z.object({
  messages: z.array(ItemCommsMessageSchema),
});

export type ItemCommsMessagesResponse = z.infer<
  typeof ItemCommsMessagesResponseSchema
>;

export const ConversationMessageSchema = z.object({
  id: z.string(),
  direction: z.string(),
  provider: z.string(),
  from: z.string().nullable(),
  senderLabel: z.string().nullable(),
  to: z.array(z.string()),
  cc: z.array(z.string()),
  bcc: z.array(z.string()),
  subject: z.string().nullable(),
  body: z.string().nullable(),
  htmlBody: z.string().nullable(),
  createdAt: z.coerce.date(),
  attachments: z.array(
    z.object({
      assetId: z.string(),
      fileName: z.string(),
    }),
  ),
});

export type ConversationMessage = z.infer<typeof ConversationMessageSchema>;

/**
 * Response for GET /comms/conversations/:conversationId/messages — the full
 * conversation behind a referenced message, in chronological order.
 */
export const ConversationMessagesResponseSchema = z.object({
  conversationId: z.string(),
  provider: z.string(),
  messages: z.array(ConversationMessageSchema),
});

export type ConversationMessagesResponse = z.infer<
  typeof ConversationMessagesResponseSchema
>;
